import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import toast from "react-hot-toast";
import {
  useGetDiamondSizesQuery,
  useCreateDiamondSizeMutation,
  useUpdateDiamondSizeMutation,
  useDeleteDiamondSizeMutation,
  useActivateDiamondSizeMutation,
  useDeactivateDiamondSizeMutation,
} from "../toolkitServices/diamondSizeApi";
import { useGetAllGroupedDiamondAttributesQuery } from "../toolkitServices/diamondAttributeApi";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 10,
    borderColor: state.isFocused ? "#9481ce" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #9481ce" : "none",
    "&:hover": { borderColor: "#b2a6e6" },
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

const StatusBadge = ({ active }) => (
  <span
    className={[
      "text-xs px-2 py-1 rounded-full border select-none",
      active
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-gray-50 text-gray-600 border-gray-200",
    ].join(" ")}
  >
    {active ? "Active" : "Inactive"}
  </span>
);

export default function DiamondSizeMaster() {
  // filters
  const [shapeOpt, setShapeOpt] = useState(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // shapes from diamond attributes
  const { data: groupedAttrs, isLoading: attrLoading, isError: attrErr } =
    useGetAllGroupedDiamondAttributesQuery();

  const shapeOptions = useMemo(
    () => (groupedAttrs?.data?.shape || []).map((a) => ({ value: a._id, label: a.name })),
    [groupedAttrs]
  );

  const params = useMemo(
    () => ({
      page,
      limit,
      shape: shapeOpt?.value || "",
      q: debouncedQ,
      sortBy: "createdAt",
      sortOrder: "desc",
    }),
    [page, limit, shapeOpt, debouncedQ]
  );

  const { data, isFetching, isError, error } = useGetDiamondSizesQuery(params, {
    refetchOnMountOrArgChange: true,
  });

  const rows = data?.data || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  // mutations
  const [createRow, { isLoading: creating }] = useCreateDiamondSizeMutation();
  const [updateRow, { isLoading: updating }] = useUpdateDiamondSizeMutation();
  const [deleteRow, { isLoading: deleting }] = useDeleteDiamondSizeMutation();
  const [activateRow] = useActivateDiamondSizeMutation();
  const [deactivateRow] = useDeactivateDiamondSizeMutation();

  // modal state
  const [openModal, setOpenModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [toggleBusy, setToggleBusy] = useState(null);

  const onAdd = () => {
    setEditRow(null);
    setOpenModal(true);
  };
  const onEdit = (r) => {
    setEditRow(r);
    setOpenModal(true);
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Delete size "${r.size}"?`)) return;
    try {
      await deleteRow(r._id).unwrap();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.data?.message || "Delete failed");
    }
  };

  const onToggleActive = async (r) => {
    setToggleBusy(r._id);
    try {
      if (r.isActive) {
        await deactivateRow(r._id).unwrap();
        toast.success("Marked inactive");
      } else {
        await activateRow(r._id).unwrap();
        toast.success("Marked active");
      }
    } catch (e) {
      toast.error(e?.data?.message || "Update failed");
    } finally {
      setToggleBusy(null);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-xl">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center px-4 py-3 bg-[#9481ce] rounded-t-xl text-white">
        <div>
          <h2 className="text-lg font-semibold">Diamond Sizes</h2>
          <p className="text-[11px] opacity-90">Shape-wise size master</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[220px]">
            <Select
              isClearable
              isDisabled={attrLoading || attrErr}
              value={shapeOpt}
              onChange={(opt) => {
                setShapeOpt(opt);
                setPage(1);
              }}
              options={shapeOptions}
              placeholder={attrLoading ? "Loading shapes…" : "Filter: Shape"}
              styles={selectStyles}
            />
          </div>

          <input
            type="text"
            placeholder="Search size…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1 rounded-md text-sm text-gray-800 focus:outline-none bg-white"
          />

          <button
            onClick={onAdd}
            className="bg-white text-[#9481ce] px-4 py-1.5 rounded-md text-sm font-medium hover:bg-violet-100"
          >
            + Add New
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-[calc(100vh-220px)] overflow-y-auto">
        <table className="min-w-[720px] w-full text-sm text-left">
          <thead className="bg-[#e6e1f5] text-[#4b3d91] uppercase text-xs sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3">Shape</th>
              <th className="px-4 py-3">Size</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            {/* error */}
            {isError && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-rose-600">
                  {error?.data?.message || "Failed to load"}
                </td>
              </tr>
            )}

            {/* skeleton */}
            {isFetching &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-t">
                  <td className="px-4 py-3"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-4 w-40 bg-gray-200 rounded animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-4 w-36 bg-gray-200 rounded animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-4 w-20 bg-gray-200 rounded animate-pulse" /></td>
                  <td className="px-4 py-3 text-right"><div className="h-8 w-24 bg-gray-200 rounded animate-pulse" /></td>
                </tr>
              ))}

            {/* empty */}
            {!isFetching && rows.length === 0 && !isError && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No sizes found.
                </td>
              </tr>
            )}

            {/* rows */}
            <AnimatePresence>
              {!isFetching &&
                rows.map((r, idx) => (
                  <motion.tr
                    key={r._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.22, delay: idx * 0.02 }}
                    className="border-t hover:bg-[#f4f0ff] transition"
                  >
                    <td className="px-4 py-2">{r.shapeName || "-"}</td>
                    <td className="px-4 py-2">{r.size}</td>
                    <td className="px-4 py-2 text-gray-500">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge active={r.isActive} />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2 justify-end">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onEdit(r)}
                          className="text-[#9481ce] text-sm px-3 py-1 rounded border hover:bg-[#9481ce]/10"
                          title="Edit"
                        >
                          Edit
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onToggleActive(r)}
                          disabled={toggleBusy === r._id}
                          className={[
                            "text-sm px-3 py-1 rounded border",
                            r.isActive
                              ? "text-rose-600 border-rose-200 hover:bg-rose-50"
                              : "text-emerald-700 border-emerald-200 hover:bg-emerald-50",
                            toggleBusy === r._id && "opacity-60",
                          ].join(" ")}
                          title={r.isActive ? "Deactivate" : "Activate"}
                        >
                          {r.isActive ? "Deactivate" : "Activate"}
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onDelete(r)}
                          disabled={deleting}
                          className="text-[#9481ce] text-sm px-3 py-1 rounded border hover:bg-[#9481ce]/10 disabled:opacity-60"
                          title="Delete"
                        >
                          Delete
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
            </AnimatePresence>
          </tbody>

          {rows.length > 0 && (
            <tfoot>
              <tr className="bg-[#f6f3ff] border-t">
                <td className="px-4 py-2 text-xs text-gray-600" colSpan={5}>
                  Total: <b>{total}</b>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {rows.length > 0 && (
        <div className="px-4 py-2 flex items-center justify-between border-t rounded-b-xl bg-white">
          <span className="text-sm text-gray-600">
            Page <b>{page}</b> / {pages}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 rounded border hover:bg-gray-100 disabled:opacity-50"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <button
              className="px-3 py-1.5 rounded border hover:bg-gray-100 disabled:opacity-50"
              disabled={page >= pages || isFetching}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
            >
              Next
            </button>

            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="px-2 py-1.5 border rounded-md text-sm"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {openModal && (
          <SizeModal
            editRow={editRow}
            shapeOptions={shapeOptions}
            onClose={() => setOpenModal(false)}
            onSubmit={async (payload) => {
              try {
                if (editRow) {
                  await updateRow({ id: editRow._id, ...payload }).unwrap();
                  toast.success("Updated");
                } else {
                  await createRow(payload).unwrap();
                  toast.success("Created");
                }
                setOpenModal(false);
              } catch (e) {
                toast.error(e?.data?.message || "Save failed");
              }
            }}
            busy={creating || updating}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Modal ---------- */
function SizeModal({ editRow, onClose, onSubmit, busy, shapeOptions }) {
  const [shape, setShape] = useState(
    editRow
      ? shapeOptions.find((o) => String(o.value) === String(editRow?.shape?._id || editRow?.shape))
      : null
  );
  const [size, setSize] = useState(editRow?.size || "");
  const [isActive, setIsActive] = useState(
    typeof editRow?.isActive === "boolean" ? editRow.isActive : true
  );

  const valid = shape && size.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40">
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.98 }}
        className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden"
      >
        <div className="px-5 py-3 bg-[#9481ce] text-white flex items-center justify-between">
          <h3 className="text-base font-semibold">{editRow ? "Edit Size" : "Add Size"}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/20" title="Close">
            ✕
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Diamond Shape *</label>
            <Select
              isClearable
              options={shapeOptions}
              value={shape}
              onChange={(opt) => setShape(opt)}
              styles={selectStyles}
              placeholder="Select shape"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Size *</label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="e.g., 1.10 mm / 1.20 mm / -11+8"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              autoFocus
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="active"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <label htmlFor="active" className="text-sm">Active</label>
          </div>
        </div>

        <div className="px-5 pb-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-gray-200 text-gray-800"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit({ shape, size: size.trim(), isActive })}
            className="px-4 py-2 rounded-md bg-[#9481ce] text-white disabled:opacity-60"
            disabled={!valid || busy}
          >
            {editRow ? "Update" : "Create"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
